/**
 * Analytics helpers. Plausible (cookieless) is primary; Meta CAPI fires
 * server-side for revenue events. Event taxonomy is the canonical one in
 * docs/03-funnel/analytics-events.md — do NOT invent new events here.
 */

export const EVENTS = {
  landing_viewed: "landing_viewed",
  hero_variant_viewed: "hero_variant_viewed",
  hero_cta_clicked: "hero_cta_clicked",
  result_preview_viewed: "result_preview_viewed",
  // Deprecated in Sprint 9 (ADR-017). Chip row was replaced by ArchetypeGrid
  // in Sprint 8; no code fires this. Key retained for back-compat reads.
  archetype_chip_expanded: "archetype_chip_expanded",
  // Sprint 9 (ADR-017): per-card viewport-intersection beacon on ArchetypeGrid.
  archetype_card_viewed: "archetype_card_viewed",
  quiz_started: "quiz_started",
  quiz_question_viewed: "quiz_question_viewed",
  quiz_answer_selected: "quiz_answer_selected",
  quiz_question_answered: "quiz_question_answered",
  quiz_back_used: "quiz_back_used",
  quiz_abandoned: "quiz_abandoned",
  // Sprint 16 (F1): mid-quiz income question + partner-income toggle.
  quiz_income_answered: "quiz_income_answered",
  quiz_finished: "quiz_finished",
  result_viewed: "result_viewed",
  methodology_expanded: "methodology_expanded",
  report_preview_viewed: "report_preview_viewed",
  // Deprecated in Sprint 4 (ADR-010). No new code fires this; the key is
  // retained for back-compat with any cached clients still posting it.
  paid_audit_notify_clicked: "paid_audit_notify_clicked",
  paywall_viewed: "paywall_viewed",
  tip_option_changed: "tip_option_changed",
  paywall_purchase_intent_clicked: "paywall_purchase_intent_clicked",
  result_share_clicked: "result_share_clicked",
  // Sprint 5 (ADR-013) result-page hooks.
  loss_ticker_seen: "loss_ticker_seen",
  // Sprint 17 S5 F2: GlobalLeakCounter impression (landing page).
  leak_counter_visible: "leak_counter_visible",
  finding_card_locked_clicked: "finding_card_locked_clicked",
  quiz_email_submitted: "quiz_email_submitted",
  checkout_viewed: "checkout_viewed",
  purchase_succeeded: "purchase_succeeded",
  report_generated: "report_generated",
  report_delivered: "report_delivered",
  subscription_started: "subscription_started",
  workbook_purchased: "workbook_purchased",
  couples_added: "couples_added",
  refund_issued: "refund_issued",
  subscription_cancelled: "subscription_cancelled",
  // Sprint 11 (ADR-020) post-checkout result-page state hooks.
  payment_landing_viewed: "payment_landing_viewed",
  payment_state_transitioned: "payment_state_transitioned",
  // Sprint 14 (ADR-032 §Item 3) result-page email gate (F11).
  email_gate_viewed: "email_gate_viewed",
  email_gate_submitted: "email_gate_submitted",
  email_gate_completed: "email_gate_completed",
  // Sprint 17 (F11/ADR-039) full-page result-page gate events.
  // result_email_gate_viewed fires once per session on mount (impression).
  // result_email_gate_submitted fires on valid submit with archetype_key,
  // time_from_quiz_end_seconds, and privacy-safe email_domain.
  result_email_gate_viewed: "result_email_gate_viewed",
  result_email_gate_submitted: "result_email_gate_submitted",
  // Sprint 15 F7/F8 P0 hotfix — quiz load + finish state-transition diagnostics.
  // A `quiz_loading_started` with no matching `quiz_loading_resolved` is the
  // F7 loader-freeze signal; the finishing_* chain localizes where finish()
  // stalls (see docs/00-project/sprint-15-telemetry-watch.md §7).
  quiz_loading_started: "quiz_loading_started",
  quiz_loading_resolved: "quiz_loading_resolved",
  quiz_finishing_started: "quiz_finishing_started",
  quiz_finishing_fetch_started: "quiz_finishing_fetch_started",
  quiz_finishing_fetch_ok: "quiz_finishing_fetch_ok",
  quiz_finishing_fetch_failed: "quiz_finishing_fetch_failed",
  quiz_finishing_routed: "quiz_finishing_routed",
  // Sprint 15 (F32) landing testimonial rotation — one fire per unique
  // testimonial shown as the rotation advances.
  testimonial_rotation_view: "testimonial_rotation_view",
  // Sprint 16 (F37) landing-hero Sparrow speech bubbles — one fire the first
  // time the bubble block scrolls into view.
  sparrow_hero_bubble_visible: "sparrow_hero_bubble_visible",
  // Sprint 18 F16 / ADR-036: price A/B test events.
  // All four carry `bucket_id` and `price_cents` props for funnel attribution.
  // price_bucket_assigned fires (server-side, via logEvent) on the first call
  //   to /api/unlock/intent when a new bucket is written to the submission row.
  // paywall_view fires client-side in PaywallCard on mount (deduplicated/session).
  // checkout_started fires in /api/checkout/create-session after order creation.
  // purchase_completed fires in /api/webhook/keepz on confirmed SUCCESS status.
  price_bucket_assigned: "price_bucket_assigned",
  paywall_view: "paywall_view",
  checkout_started: "checkout_started",
  purchase_completed: "purchase_completed",
} as const;

export type AnalyticsEvent = keyof typeof EVENTS;

/** Client-side fire (called from "use client" components). */
export function track(event: AnalyticsEvent, props?: Record<string, string | number | boolean>) {
  if (typeof window === "undefined") return;
  const w = window as unknown as {
    plausible?: (e: string, opts?: { props?: Record<string, unknown> }) => void;
    dataLayer?: Array<Record<string, unknown>>;
  };
  w.plausible?.(event, props ? { props } : undefined);
  // Mirror into the Google Tag Manager dataLayer so GTM tags (and the
  // downstream ad platforms they fan out to, e.g. Pinterest) can trigger on
  // the same canonical funnel events. The pushed `event` name matches EVENTS,
  // so a GTM "Custom Event" trigger keys off it directly; props (incl. value
  // fields like base_amount_minor / price_cents) ride along as variables.
  // Safe before GTM loads — it drains the queued pushes on init.
  (w.dataLayer = w.dataLayer || []).push({ event, ...(props ?? {}) });
}
